import type { ColumnType } from "antd/es/table";
import type { SortOrder } from "antd/es/table/interface";
import type { TFunction } from "i18next";
import { Link } from "../../../router/LocaleLink";
import { PathGenerators } from "../../../router/paths";
import type { EtfRow, EtfSortKey } from "../../../api/services/etf-screener.type";
import { NumericCell } from "../StockScreener/NumericCell";
import styles from "../StockScreener/stockScreener.module.css";
import { EtfVerdictBadge } from "./EtfVerdictBadge";
import type { EtfSort, EtfViewTab } from "./etfFilters";
import {
  formatAum,
  formatCount,
  formatEtfPercent,
  formatExpenseRatio,
  formatNaiveDate,
} from "./etfFormat";
import etfStyles from "./etfScreener.module.css";


const numeric = (col: ColumnType<EtfRow>): ColumnType<EtfRow> => ({
  ...col,
  align: "right",
  onCell: () => ({ className: styles.numericCol }),
  onHeaderCell: () => ({ className: styles.numericCol }),
});

const percentCell = (value: number | null) => {
  const text = formatEtfPercent(value);
  return <NumericCell title={text}>{text}</NumericCell>;
};

/**
 * Columns: five fixed (No., Fund, Venue, Akinda fund screen, Coverage) plus
 * one group per view tab. Only the eight keys the API sorts on get a sorter;
 * every other header stays inert, because an unsupported sort is a 400.
 */
export const getEtfColumns = (
  t: TFunction,
  view: EtfViewTab,
  sort: EtfSort,
  page: number,
  pageSize: number,
): ColumnType<EtfRow>[] => {
  const p = "pages.etfScreener.";
  const rowOffset = (page - 1) * pageSize;
  const sortable = (key: EtfSortKey): Partial<ColumnType<EtfRow>> => ({
    key,
    sorter: true,
    sortDirections: ["descend", "ascend"],
    sortOrder: (sort.key === key
      ? sort.order === "asc"
        ? "ascend"
        : "descend"
      : null) as SortOrder,
  });

  const fixed: ColumnType<EtfRow>[] = [
    numeric({
      title: t(`${p}columns.no`),
      key: "index",
      width: 44,
      render: (_: unknown, __: EtfRow, index: number) => (
        <span style={{ color: "var(--app-text-secondary)" }}>{rowOffset + index + 1}</span>
      ),
    }),
    {
      ...sortable("ticker"),
      title: t(`${p}columns.fund`),
      dataIndex: "ticker",
      width: 260,
      ellipsis: true,
      render: (_: string, row: EtfRow) => (
        <div className={etfStyles.fundCell}>
          <span className={etfStyles.fundTicker}>
            {/* A real link, so the fund page is crawlable and opens in a new tab. */}
            <Link className={etfStyles.fundTickerLink} to={PathGenerators.ETF(row.ticker)}>
              {row.ticker}
            </Link>
            {row.holdingsTotal === 1 && (
              <span className={etfStyles.singleBadge} title={t(`${p}singleHoldingHint`)}>
                {t(`${p}singleHolding`)}
              </span>
            )}
          </span>
          <span className={etfStyles.fundName} title={row.fundName ?? undefined}>
            {row.fundName ?? "—"}
          </span>
        </div>
      ),
    },
    {
      title: t(`${p}columns.venue`),
      key: "venue",
      width: 76,
      render: (_: unknown, row: EtfRow) =>
        row.venue ? t(`${p}venues.${row.venue}`) : "—",
    },
    {
      title: (
        <span title={t(`${p}fundScreenHint`)}>{t(`${p}columns.fundScreen`)}</span>
      ),
      key: "status",
      width: 150,
      render: (_: unknown, row: EtfRow) => <EtfVerdictBadge status={row.status} compact />,
    },
    numeric({
      ...sortable("coverage_weight_perc"),
      title: <span title={t(`${p}coverageHint`)}>{t(`${p}columns.coverage`)}</span>,
      dataIndex: "coverageWeightPerc",
      width: 96,
      render: percentCell,
    }),
  ];

  const groups: Record<EtfViewTab, ColumnType<EtfRow>[]> = {
    weights: [
      numeric({ title: t(`${p}columns.halalWeight`), key: "compliant", dataIndex: "compliantWeightPerc", width: 92, render: percentCell }),
      numeric({ title: t(`${p}columns.doubtfulWeight`), key: "doubtful", dataIndex: "doubtfulWeightPerc", width: 92, render: percentCell }),
      numeric({
        ...sortable("non_compliant_weight_perc"),
        title: t(`${p}columns.notHalalWeight`),
        dataIndex: "nonCompliantWeightPerc",
        width: 104,
        render: percentCell,
      }),
      numeric({ title: t(`${p}columns.notRatedWeight`), key: "unscreened", dataIndex: "unscreenedWeightPerc", width: 96, render: percentCell }),
    ],
    rulebooks: (["Djim", "Ftse", "Msci", "Sp"] as const).map((book) => ({
      title: t(`${p}columns.rulebook${book}`),
      key: `status${book}`,
      width: 150,
      render: (_: unknown, row: EtfRow) => (
        <EtfVerdictBadge status={row[`status${book}`]} compact />
      ),
    })),
    fund: [
      numeric({
        ...sortable("expense_ratio"),
        title: t(`${p}columns.expense`),
        dataIndex: "expenseRatio",
        width: 92,
        render: (value: number | null) => <NumericCell>{formatExpenseRatio(value)}</NumericCell>,
      }),
      numeric({
        ...sortable("aum"),
        title: t(`${p}columns.aum`),
        dataIndex: "aum",
        width: 120,
        render: (_: number | null, row: EtfRow) => (
          <NumericCell>{formatAum(row.aum, row.reportingCurrency)}</NumericCell>
        ),
      }),
      {
        title: t(`${p}columns.issuer`),
        key: "issuer",
        dataIndex: "issuer",
        width: 150,
        ellipsis: true,
        render: (value: string | null) => value ?? "—",
      },
    ],
    basket: [
      numeric({
        ...sortable("holdings_total"),
        title: t(`${p}columns.holdings`),
        dataIndex: "holdingsTotal",
        width: 92,
        render: (value: number | null) => <NumericCell>{formatCount(value)}</NumericCell>,
      }),
      {
        title: t(`${p}columns.basketAsOf`),
        key: "holdingsAsOf",
        dataIndex: "holdingsAsOf",
        width: 116,
        render: (value: string | null) => formatNaiveDate(value),
      },
      {
        ...sortable("last_updated"),
        title: t(`${p}columns.lastUpdated`),
        dataIndex: "lastUpdated",
        width: 116,
        render: (value: string | null) => formatNaiveDate(value),
      },
    ],
  };

  return [...fixed, ...groups[view]];
};

export const getEtfTableScrollX = (columns: ColumnType<EtfRow>[]): number =>
  columns.reduce((sum, col) => sum + (typeof col.width === "number" ? col.width : 96), 0);
